import { Injectable } from '@angular/core';
import { ContentService } from '@blk/cwp-ng-framework';
import { InjectConfig, SharedConfigKey } from '@ddt-web/config-layer';
import { CellData, ComponentData, DisplayDataFactoryService } from '@ddt-web/data-service';
import { RulesEngine } from '@ddt-web/rules-engine';
import { ApplicationStateStoreProxyService } from '@ddt-web/shared-stores';
import { UrlQueryParamService } from '@ddt-web/url';
import * as cloneDeep from 'lodash/cloneDeep';
import * as groupBy from 'lodash/groupBy';
import * as orderBy from 'lodash/orderBy';
import * as partition from 'lodash/partition';
import * as uniqBy from 'lodash/uniqBy';
import { ReplaySubject } from 'rxjs';
import { filter } from 'rxjs/operators';

import { ProposalMetricDeltaCalculation } from '../models/proposal-metric-delta-calculation.enum';
import { ProposalMetricsMappingInterface } from '../models/proposal-metric.interface';

@Injectable({
	providedIn: 'root'
})
export class ProposalMetricsCalculationService {

	@InjectConfig(SharedConfigKey.PROPOSAL_METRIC_MAPPING) private readonly proposalMetricMapping: ProposalMetricsMappingInterface[] = [];
	@InjectConfig(SharedConfigKey.PROPOSAL_ALLOCATION_METRIC_MAX_ASSET_CLASSES_CHANGES) private readonly maxMetricAssetClassesChanges: number = 2;

	private readonly CUSTOM_ALLOCATION_LOGIC = 'CUSTOM_ALLOCATION_LOGIC';

	public ids$: ReplaySubject<string[]> = new ReplaySubject(1);
	private fee: number;
	private metricStatementConnectionWord;
	private metricChangeConnectionWord;

	constructor(
		private urlQueryParamService: UrlQueryParamService,
		private displayDataFactoryService: DisplayDataFactoryService,
		private rulesEngine: RulesEngine,
		private contentService: ContentService,
		private applicationStateStoreProxyService: ApplicationStateStoreProxyService
	) { }

	public init(): void {
		this.metricStatementConnectionWord = this.contentService.get('PROPOSAL_METRIC_STATEMENT_CONNECTION_WORD', 'and');
		this.metricChangeConnectionWord = this.contentService.get('PROPOSAL_METRIC_CHANGE_CONNECTION_WORD', 'and');

		// eslint-disable-next-line @nrwl/nx/workspace/killSubscriptionRule
		this.urlQueryParamService.queryParams$.pipe(
			filter(queryParams => !!queryParams.portfolios)
		).subscribe(queryParams => {
			// reset proposalState whenever there are any changes in the analysis
			this.applicationStateStoreProxyService.updateProposalMetrics({ metrics: [] });
			this.applicationStateStoreProxyService.updateProposalPortfolioName(null);
			this.applicationStateStoreProxyService.updateProposalPurpose({ purpose: {} });

			if (queryParams.portfolios.length === 2) {
				this.ids$.next(queryParams.portfolios);
				this.fee = queryParams?.fee ? +queryParams?.fee / 100 : null;
			}
		});

		// eslint-disable-next-line @nrwl/nx/workspace/killSubscriptionRule
		this.displayDataFactoryService.getDisplayableComponentData('proposal.metrics', this.ids$, this.contentService)
			.pipe(filter(componentData => componentData?.success))
			.subscribe(componentData => {
				// get proposal portfolio name - should be the second portfolio in the analysis
				const proposalPortfolioName = componentData.portfolioData?.[1]?.field?.name?.formattedValue;
				this.applicationStateStoreProxyService.updateProposalPortfolioName(proposalPortfolioName);

				const factData = Object.fromEntries(this.calculateFactDelta(componentData));

				// get rules & update output with icon and section dependency
				const rules = this.proposalMetricMapping.reduce((rules, metricMapping) => {
					const metricRules = (metricMapping.rules ?? []).filter(rule => !!rule);
					const updatedRules = metricRules.map(rule => {
						return {
							...rule,
							output: {
								...rule.output,
								icon: metricMapping.icon,
								componentSectionDependency: metricMapping.componentSectionDependency,
								order: metricMapping.order
							}
						};

					});
					rules.push(...updatedRules);

					return rules;
				}, []);

				const output = this.rulesEngine.run(cloneDeep(rules), factData);
				const metrics = output.map(outputResult => outputResult.output).filter(metric => !!metric);

				// post metric calculation
				const [assetAllocationMetrics, nonAssetAllocationMetrics] = partition(metrics, metric => metric.type === this.CUSTOM_ALLOCATION_LOGIC);
				let updatedMetrics = nonAssetAllocationMetrics?.length ? this.generateNonAssetLocationMetric(nonAssetAllocationMetrics) : [];

				if (assetAllocationMetrics?.length) {
					const updatedAssetAllocationMetric = this.generateAssetLocationMetric(assetAllocationMetrics);
					updatedMetrics.unshift(updatedAssetAllocationMetric);
				}

				// apply order if provided for all metrics 
				if (updatedMetrics.every(metric => metric.order)) {
					updatedMetrics = orderBy(updatedMetrics, ['order']);
				}
				this.applicationStateStoreProxyService.updateProposalMetrics({ metrics: updatedMetrics });
			});
	}

	private generateAssetLocationMetric(assetAllocationMetrics: any): any {
		// order by criteriaPriority
		const allocationGroupByCriteriaPriority = groupBy(assetAllocationMetrics, 'criteriaPriority');

		// only take into consideration the metrics with highest criteriaPriority
		const allMetricsToDisplay = Object.values(allocationGroupByCriteriaPriority)[0];

		// take top metrics to display (based on config flag & asset class priority)
		const metricsToDisplay = uniqBy(orderBy(allMetricsToDisplay, ['assetClassPriority']), 'assetClass').slice(0, this.maxMetricAssetClassesChanges);

		// group by change
		const metricsGroupByChange = groupBy(metricsToDisplay, 'change');

		// go through all changes and dynamically create the asset allocation statement
		const allocationStatement = Object.keys(metricsGroupByChange).reduce((statement, changeKey, index) => {
			const changeSpecificMetrics = metricsGroupByChange[changeKey];

			const metricsStatement: string = changeSpecificMetrics.reduce((metricsStatement, changeResult, metricIndex) => {
				const prefix = metricIndex === 0 ? '' : this.metricStatementConnectionWord;
				return [metricsStatement, prefix, changeResult.assetClass].filter(word => !!word).join(' ');
			}, '');

			const prefix = index === 0 ? '' : this.metricChangeConnectionWord;
			const change = index === 0 ? changeKey : changeKey.toLowerCase();
			return [statement, prefix, change, metricsStatement].filter(word => !!word).join(' ');
		}, '');

		return {
			name: assetAllocationMetrics[0].name,
			componentSectionDependency: assetAllocationMetrics[0].componentSectionDependency,
			icon: assetAllocationMetrics[0].icon,
			order: assetAllocationMetrics[0].order,
			overview: allocationStatement
		};
	}

	private generateNonAssetLocationMetric(nonAssetAllocationMetrics: any): any {
		// group by name (category)
		const metricsGroupByName = groupBy(nonAssetAllocationMetrics, 'name');

		// go through all changes and dynamically create the asset allocation statement
		const metricStatements = Object.keys(metricsGroupByName).reduce((statements, sectionKey) => {
			const sectionSpecificMetrics = metricsGroupByName[sectionKey];

			const metricsStatement: string = sectionSpecificMetrics.reduce((metricsStatement, sectionResult, metricIndex) => {
				const prefix = metricIndex === 0 ? '' : this.metricStatementConnectionWord;
				const overview = metricIndex === 0 ? sectionResult.overview : sectionResult.overview?.toLowerCase();
				return [metricsStatement, prefix, overview].filter(word => !!word).join(' ');
			}, '');

			statements.push({
				name: sectionSpecificMetrics[0].name,
				componentSectionDependency: sectionSpecificMetrics[0].componentSectionDependency,
				icon: sectionSpecificMetrics[0].icon,
				order: sectionSpecificMetrics[0].order,
				overview: metricsStatement
			});

			return statements;
		}, []);

		return metricStatements;
	}

	private calculateFactDelta(componentData: ComponentData): Map<string, number> {
		const metricsCells = componentData.tabs?.[0]?.tableData?.[0].cells;

		const primaryToSecondaryMap = new Map<string, string>();
		const factData = new Map<string, number>();
		metricsCells?.[0].forEach((cell, index) => {
			const deltaCalculationType = cell?.config?.label?.attributes?.deltaCalculationType;
			// field required for net calculation on FE side; this is required because the net analysis is not pre-calculated on AWA side
			const calculatedNetFeeMetricId = cell?.config?.label?.attributes?.calculatedNetFeeMetricId;

			// set factData for all metrics
			this.setFactData(cell, index, factData, metricsCells, calculatedNetFeeMetricId, deltaCalculationType);

			const primaryMetricId = cell?.config?.label?.attributes?.fallbackForPerformanceMetricId;
			if (primaryMetricId) {
				primaryToSecondaryMap.set(primaryMetricId, cell.config.id);
			}
		});

		// validate & cleanup factData to only include primary or secondary values
		if (primaryToSecondaryMap.size) {
			Array.from(primaryToSecondaryMap.keys()).forEach(primaryMetricId => {
				const secondaryMetricId = primaryToSecondaryMap.get(primaryMetricId);
				if (!!this.fee && factData.get(primaryMetricId)) { // if fee is set & net is valid
					this.removeFactData(factData, secondaryMetricId); // remove gross metrics
				} else {
					this.removeFactData(factData, primaryMetricId); // otherwise remove net metrics
				}
			});
		}

		return factData;
	}

	private setFactData(
		cell: CellData,
		index: number,
		factData: Map<string, number>,
		metricsCells: CellData[][],
		calculatedNetFeeMetricId?: string,
		deltaCalculationType: ProposalMetricDeltaCalculation = ProposalMetricDeltaCalculation.PERCENTAGE
	): void {
		let valuePort1 = +cell.rawValue;
		let valuePort2 = +metricsCells[1][index].rawValue;

		// update values if net calculation needs to be done on FE side (not supported by awa)
		if (calculatedNetFeeMetricId && !!this.fee) {
			valuePort1 -= this.fee; // apply net fee
			valuePort2 -= this.fee; // apply net fee
		}

		let formattedDelta = NaN;
		if (deltaCalculationType === ProposalMetricDeltaCalculation.PERCENTAGE && valuePort1 !== 0) {
			const delta = ((valuePort2 - valuePort1) / Math.abs(valuePort1)) * 100;
			formattedDelta = Number(delta.toFixed(2)); // we need to round the delta to 2 decimal points to avoid situations when we show increase/decrease for 0.0000002%
		} else if (deltaCalculationType === ProposalMetricDeltaCalculation.DIFFERENCE) {
			formattedDelta = valuePort2 - valuePort1;
		}

		const metricId = calculatedNetFeeMetricId && !!this.fee ? calculatedNetFeeMetricId : cell.config.id;

		factData.set(metricId, formattedDelta);
		factData.set('INITIAL_' + metricId, valuePort1); // this value is needed in the maintained case only (when delta is 0)
	}

	private removeFactData(factData: Map<string, number>, metricId: string): void {
		factData.delete(metricId);
		factData.delete('INITIAL_' + metricId);
	}
}
